<template>
  <div id="insider-backtester-transactions-filter-tab">
    <v-container fluid pa-0>
      <v-row>
        <!-- Transaction Types -->
        <v-col cols="12" mb-4>
          <helpers-section-sub-header class="mb-2"> Transaction types: </helpers-section-sub-header>
          <v-radio-group v-model="clusterCriteria.transaction_type" row hide-details>
            <v-radio :value="1" label="All Buy transactions" color="primary" />
            <v-radio :value="4" label="Open Market purchases" color="primary" />
          </v-radio-group>
          <v-checkbox
            v-model="clusterCriteria.open_market_purchase_code"
            density="compact"
            :disabled="clusterCriteria.transaction_type != 4"
            label="Exclude Certain Open Market Purchases Only"
            hint="will exclude open market purchases made through a shareholder purchase agreement or another arrangement."
            persistent-hint
            class="mt-0"
          />
          <v-checkbox
            v-model="clusterCriteria.is_10b5"
            density="compact"
            label="Include Purchases Made under a 10b5-1 agreement (excluded by default)"
            hint="will include open market purchases made under rule 10b5-1"
            persistent-hint
            class="mt-0"
          />

          <v-radio-group v-model="clusterCriteria.direct_owner" row hide-details>
            <v-radio :value="0" label="Use Direct or Indirect Transactions" color="primary" />
            <v-radio :value="1" label="Use Direct Owner Transactions" color="primary" />
            <v-radio :value="2" label="Use Indirect Owner Transactions" color="primary" />
          </v-radio-group>
        </v-col>

        <!-- Date Range -->
        <v-col cols="12" mb-4>
          <helpers-section-sub-header>Date range:</helpers-section-sub-header>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="3">
              <v-select
                v-model="clusterCriteria.date_range"
                :items="dateRangeItems"
                item-title="text"
                variant="outlined"
                density="compact"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12">
          <v-radio-group v-model="clusterCriteria.industry_cluster" row hide-details>
            <v-radio :value="false" label="Individual Stock Cluster Buying" color="primary" />
            <v-radio :value="true" label="Sector/Industry Cluster Buying" color="primary" />
          </v-radio-group>
        </v-col>

        <!-- Types of Owners -->
        <v-col cols="12" mb-4>
          <helpers-section-sub-header class="mb-2"> Types of owners: </helpers-section-sub-header>
          <v-select
            v-model="clusterCriteria.owner_types"
            :items="ownerTypes"
            item-title="text"
            variant="outlined"
            density="compact"
            multiple
            chips
            closable-chips
            hide-details
            class="pt-0 d-inline-flex"
          />
        </v-col>

        <!-- Minimum # insiders -->
        <v-col cols="12" xl="2" mb-4>
          <helpers-section-sub-header>Minimum # insiders:</helpers-section-sub-header>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="12">
              <v-text-field
                v-model="clusterCriteria.minimum_unique_owners"
                mask="####"
                variant="outlined"
                density="compact"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- Min. Cluster Amount -->
        <v-col cols="12" xl="2" mb-4>
          <helpers-section-sub-header>Min. cluster amount:</helpers-section-sub-header>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="12">
              <v-text-field
                v-model="clusterCriteria.minimum_transaction_amount"
                type="number"
                prefix="$"
                variant="outlined"
                density="compact"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- Minimum # transactions -->
        <v-col cols="12" xl="2" mb-4>
          <helpers-section-sub-header>Minimum # transactions:</helpers-section-sub-header>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="12">
              <v-text-field
                v-model="clusterCriteria.minimum_transaction_count"
                mask="####"
                variant="outlined"
                density="compact"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" xl="2" mb-4>
          <helpers-section-sub-header>Minimum # stocks:</helpers-section-sub-header>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="12">
              <v-text-field
                v-model="clusterCriteria.minimum_stock_count"
                mask="####"
                variant="outlined"
                density="compact"
                persistent-hint
                hint="(used with sector/industry cluster buying)"
                :disabled="!clusterCriteria.industry_cluster"
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>
        <!-- Min. Cluster Amount -->
        <v-col cols="12" xl="2" mb-4>
          <helpers-section-sub-header>Min. % of Insider's Total Shares:</helpers-section-sub-header>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="12">
              <v-text-field
                v-model="clusterCriteria.minimum_percent_insider_shares"
                type="number"
                suffix="%"
                variant="outlined"
                density="compact"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>
        <!-- Min. Cluster Amount -->
        <v-col cols="12" xl="2" mb-4>
          <helpers-section-sub-header>Max. % of Insider's Total Shares:</helpers-section-sub-header>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="12">
              <v-text-field
                v-model="clusterCriteria.maximum_percent_insider_shares"
                type="number"
                suffix="%"
                variant="outlined"
                density="compact"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12">
          <v-radio-group v-model="criteria.real_time_mode">
            <v-radio
              :value="1"
              label="Backtest using insider filings that came in the previous day or before the 9:30AM Open."
            />
            <v-radio
              :value="2"
              label="Backtest simulating real-time.  Insider filings that come in any time before 3:30PM EST will be considered for that day"
            />
          </v-radio-group>
        </v-col>
        <!-- Trading Delay -->
        <v-col v-if="1 == 2" cols="12" mb-2>
          <helpers-section-sub-header>Trading delay:</helpers-section-sub-header>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="3">
              <v-text-field
                v-model="criteria.trading_delay"
                type="number"
                suffix="days"
                variant="outlined"
                density="compact"
                hint="Days after filing(s) released before being considered for backtesting"
                persistent-hint
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>
        <!-- Restrict to Buy Reversals Only -->
        <v-col cols="12">
          <v-checkbox
            v-model="clusterCriteria.buy_reversal"
            density="compact"
            label="Restrict to Buy reversals only"
            hint="Checking the buy reversal option will limit transactions to only those where an insider purchases new shares after having only sold shares during the past 18 months."
            persistent-hint
            class="mt-0"
          />
        </v-col>
        <v-col cols="12" mb-2>
          <helpers-section-sub-header class="mb-2">
            Insider Past Performance and Unusual Trades
          </helpers-section-sub-header>
          <v-row>
            <v-col cols="12" sm="6" md="3" lg="2">
              <v-text-field
                v-model="clusterCriteria.min_avg_1d_perf"
                type="number"
                variant="outlined"
                density="compact"
                label="Min. Past Avg 1d Perf"
                class="pt-0"
                style="max-width: 200px"
                suffix="%"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3" lg="2">
              <v-text-field
                v-model="clusterCriteria.min_avg_5d_perf"
                type="number"
                variant="outlined"
                density="compact"
                label="Min. Past Avg 5d Perf"
                class="pt-0"
                style="max-width: 200px"
                suffix="%"
              />
            </v-col>

            <v-col cols="12" sm="6" md="3" lg="2">
              <v-text-field
                v-model="clusterCriteria.min_avg_1m_perf"
                type="number"
                variant="outlined"
                density="compact"
                label="Min. Past Avg 1m Perf"
                class="pt-0"
                style="max-width: 200px"
                suffix="%"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3" lg="2">
              <v-text-field
                v-model="clusterCriteria.min_avg_6m_perf"
                type="number"
                variant="outlined"
                density="compact"
                label="Min. Past Avg 6m Perf"
                class="pt-0"
                style="max-width: 200px"
                suffix="%"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3" lg="2">
              <v-text-field
                v-model="clusterCriteria.min_avg_1y_perf"
                type="number"
                variant="outlined"
                density="compact"
                label="Min. Past Avg 1y Perf"
                class="pt-0"
                style="max-width: 200px"
                suffix="%"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3" lg="2">
              <v-text-field
                v-model="clusterCriteria.min_past_trades"
                type="number"
                variant="outlined"
                density="compact"
                label="Min. # of Past Trades"
                class="pt-0"
                style="max-width: 200px"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3" lg="2">
              <v-text-field
                v-model="clusterCriteria.max_past_trades"
                type="number"
                variant="outlined"
                density="compact"
                label="Max. # of Past Trades"
                class="pt-0"
                style="max-width: 200px"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3" lg="3">
              <v-text-field
                v-model="clusterCriteria.days_since_last_buy"
                type="number"
                variant="outlined"
                density="compact"
                label="Min. # of days since last buy"
                class="pt-0"
                style="max-width: 250px"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
// Modules
import { mapStoreState } from '@/store/helpers'
import { useUserStore, useAppStore, useWwStore } from '@/store'
// Modules
import { mapWritableState } from 'pinia'

export default {
  data() {
    return {
      dateRangeItems: [
        { value: 0, text: 'Past Day' },
        { value: 1, text: 'Past 3 Days' },
        { value: 2, text: 'Past 7 Days' },
        { value: 6, text: 'Past 2 Weeks' },
        { value: 3, text: 'Past Month' } /*,
        { value: 4, text: 'Past 3 Months' } */
      ],
      ownerTypes: [
        { value: 1, text: '>10% Owner' },
        { value: 2, text: 'Director' },
        { value: 3, text: 'Officer' },
        { value: 5, text: 'C-Level Officer (CEO, COO, CFO, etc.)' },
        { value: 4, text: 'Other Owner' }
      ],
      marketCaps: [
        { name: 'Over $200 billion', id: 1 },
        { name: '$100 billion - $200 billion', id: 2 },
        { name: '$50 billion - $100 billion', id: 3 },
        { name: '$20 billion - $50 billion', id: 4 },
        { name: '$10 billion - $20 billion', id: 5 },
        { name: '$2 billion - $10 billion', id: 6 },
        { name: '$250 million - $2 billion', id: 7 },
        { name: 'Below $250 million', id: 8 }
      ]
    }
  },

  computed: {
    ...mapStoreState('ww', ['sectors']),
    ...mapWritableState(useAppStore, ['whaleTraderCriteria', 'signalView', 'exit_time_periods']),
    criteria() {
      return this.whaleTraderCriteria.signals.insider_signal
    },
    appcriteria() {
      return this.whaleTraderCriteria
    },
    clusterCriteria() {
      return this.whaleTraderCriteria.signals.insider_signal.entry_cluster_buy_criteria
    },
    enabled() {
      return this.whaleTraderCriteria.signals.insider_signal.enabled
    }
    /*...mapFields({
      clusterCriteria: 'app.whaleTraderCriteria.signals.insider_signal.entry_cluster_buy_criteria',
      appcriteria: 'app.whaleTraderCriteria',
      criteria: 'app.whaleTraderCriteria.signals.insider_signal',
      enabled: 'app.whaleTraderCriteria.signals.insider_signal.enabled'
    })*/
  },

  mounted() {
    useWwStore().loadSectors()
    if (this.clusterCriteria) {
      if (typeof this.clusterCriteria.industry_cluster == 'undefined') {
        this.clusterCriteria.industry_cluster = false
      }
    }
  }
}
</script>
